import { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Paper, Grid, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  Switch, Tabs, Tab, Tooltip
} from "@mui/material";
import { Business, PersonAdd, Upload, Edit, Add, VpnKey, Numbers, Download, DeleteForever } from "@mui/icons-material";

const API = process.env.REACT_APP_API_URL || "";

const emptyEmpresa = {
  nombre: "", email: "", password: "",
  razonSocial: "", nombreComercial: "", ruc: "",
  direccion: "", sucursal: "", establecimiento: "001", ptoEmision: "001",
  obligadoContabilidad: "NO", contribuyenteRimpe: "",
  tipoContribuyente: "CONTRIBUYENTE NEGOCIO POPULAR - RÉGIMEN RIMPE",
  ambiente: "1", secuencialInicial: "1",
  tipoPlan: "mensual",
};

const emptyCajero = { nombre: "", email: "", password: "", empresaId: "" };

export default function UsersPage() {
  const [tab, setTab] = useState(0);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", sev: "success" });

  // Crear empresa
  const [form, setForm] = useState({ ...emptyEmpresa });
  const [logoFile, setLogoFile] = useState(null);
  const [firmaFile, setFirmaFile] = useState(null);
  const [firmaPassword, setFirmaPassword] = useState("");

  // Crear cajero
  const [cajeroForm, setCajeroForm] = useState({ ...emptyCajero });

  // Dialogs para editar
  const [dialogFirma, setDialogFirma] = useState({ open: false, userId: null });
  const [dialogSecuencial, setDialogSecuencial] = useState({ open: false, userId: null, empresaId: null, secuencial: "" });
  const [dialogEditar, setDialogEditar] = useState({ open: false, empresa: null });
  const [editForm, setEditForm] = useState({});
  const [editLogoFile, setEditLogoFile] = useState(null);
  const [editFirmaFile, setEditFirmaFile] = useState(null);
  const [editFirmaPassword, setEditFirmaPassword] = useState("");
  const [puntosEmision, setPuntosEmision] = useState([]);
  const [nuevoPunto, setNuevoPunto] = useState({ establecimiento: "", puntoEmision: "", nombre: "", secuencialInicial: "1" });

  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const show = (msg, sev = "success") => setSnackbar({ open: true, msg, sev });

  useEffect(() => { cargarUsuarios(); }, []);

  const cargarUsuarios = async () => {
    try {
      const res = await fetch(`${API}/api/usuarios`, { headers });
      const data = await res.json();
      if (Array.isArray(data)) setUsuarios(data);
    } catch (e) { console.error(e); }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const crearEmpresa = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.ruc || !form.razonSocial) {
      return show("Email, contraseña, RUC y razón social son requeridos", "error");
    }
    if (!/^\d{13}$/.test(form.ruc)) {
      return show("El RUC debe tener 13 dígitos", "error");
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/empresa/crear`, {
        method: "POST", headers, body: JSON.stringify(form),
      });
      const data = await res.json();

      if (data.success) {
        const userId = data.user.id;

        // Subir logo si se seleccionó
        if (logoFile) {
          const logoData = new FormData();
          logoData.append("logo", logoFile);
          logoData.append("userId", userId);
          await fetch(`${API}/api/empresa/logo`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: logoData,
          });
        }

        // Subir firma si se seleccionó
        if (firmaFile && firmaPassword) {
          const firmaData = new FormData();
          firmaData.append("firma", firmaFile);
          firmaData.append("password", firmaPassword);
          // Subir firma como el nuevo usuario (necesitamos un endpoint especial)
          await fetch(`${API}/api/usuarios/${userId}/firma`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: firmaData,
          });
        }

        show("Empresa creada correctamente");
        setForm({ ...emptyEmpresa });
        setLogoFile(null);
        setFirmaFile(null);
        setFirmaPassword("");
        cargarUsuarios();
      } else {
        show(data.error || "Error al crear empresa", "error");
      }
    } catch (err) {
      show("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  const crearCajero = async (e) => {
    e.preventDefault();
    if (!cajeroForm.nombre || !cajeroForm.email || !cajeroForm.password || !cajeroForm.empresaId) {
      return show("Todos los campos son requeridos", "error");
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/cajero/crear`, {
        method: "POST", headers, body: JSON.stringify(cajeroForm),
      });
      const data = await res.json();
      if (data.success) {
        show("Cajero creado correctamente");
        setCajeroForm({ ...emptyCajero });
        cargarUsuarios();
      } else {
        show(data.error, "error");
      }
    } catch (err) {
      show("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleActivo = async (id, activo) => {
    try {
      await fetch(`${API}/api/usuarios/${id}/active`, {
        method: "PUT", headers, body: JSON.stringify({ active: !activo }),
      });
      cargarUsuarios();
    } catch (e) { console.error(e); }
  };

  const abrirDialogFirma = (userId) => {
    setDialogFirma({ open: true, userId });
    setEditFirmaFile(null);
    setEditFirmaPassword("");
  };

  const subirFirma = async () => {
    if (!editFirmaFile || !editFirmaPassword) {
      return show("Debe seleccionar un archivo .p12 y su contraseña", "error");
    }
    setLoading(true);
    try {
      const firmaData = new FormData();
      firmaData.append("firma", editFirmaFile);
      firmaData.append("password", editFirmaPassword);
      
      const res = await fetch(`${API}/api/usuarios/${dialogFirma.userId}/firma`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: firmaData,
      });
      
      const data = await res.json();
      if (data.success) {
        show("Firma electrónica actualizada correctamente");
        setDialogFirma({ open: false, userId: null });
        cargarUsuarios();
      } else {
        show(data.error || "Error al subir firma", "error");
      }
    } catch (err) {
      show("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  const abrirDialogSecuencial = (userId, empresaId, secuencialActual) => {
    setDialogSecuencial({ open: true, userId, empresaId, secuencial: secuencialActual || "1" });
  };

  const actualizarSecuencial = async () => {
    setLoading(true);
    try {
      // Actualizar secuencial de facturas
      if (dialogSecuencial.secuencial && parseInt(dialogSecuencial.secuencial) >= 1) {
        await fetch(`${API}/api/empresa/${dialogSecuencial.empresaId}/secuencial`, {
          method: "PUT", headers,
          body: JSON.stringify({ secuencialInicial: dialogSecuencial.secuencial }),
        });
      }
      
      // Actualizar secuencial de guías de remisión
      if (dialogSecuencial.secuencialGuia && parseInt(dialogSecuencial.secuencialGuia) >= 1) {
        await fetch(`${API}/api/empresa/${dialogSecuencial.empresaId}/secuencial-guia`, {
          method: "PUT", headers,
          body: JSON.stringify({ secuencialInicial: dialogSecuencial.secuencialGuia }),
        });
      }
      
      // Actualizar secuencial de liquidaciones
      if (dialogSecuencial.secuencialLiquidacion && parseInt(dialogSecuencial.secuencialLiquidacion) >= 1) {
        await fetch(`${API}/api/empresa/${dialogSecuencial.empresaId}/secuencial-liquidacion`, {
          method: "PUT", headers,
          body: JSON.stringify({ secuencialInicial: dialogSecuencial.secuencialLiquidacion }),
        });
      }

      show("Secuenciales actualizados correctamente");
      setDialogSecuencial({ open: false, userId: null, empresaId: null, secuencial: "" });
      cargarUsuarios();
    } catch (err) {
      show("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  const abrirDialogEditar = (u) => {
    setEditForm({
      nombre: u.nombre || "",
      email: u.email || "",
      newPassword: "",
      razonSocial: u.empresa?.razonSocial || "",
      nombreComercial: u.empresa?.nombreComercial || "",
      direccion: u.empresa?.direccion || "",
      sucursal: u.empresa?.sucursal || "",
      establecimiento: u.empresa?.establecimiento || "001",
      ptoEmision: u.empresa?.ptoEmision || "001",
      obligadoContabilidad: u.empresa?.obligadoContabilidad || "NO",
      contribuyenteRimpe: u.empresa?.contribuyenteRimpe || "",
      tipoContribuyente: u.empresa?.tipoContribuyente || "",
      ambiente: u.empresa?.ambiente || "2",
      tipoPlan: u.empresa?.tipoPlan || "mensual",
    });
    setEditLogoFile(null);
    setDialogEditar({ open: true, empresa: u });
    // Cargar puntos de emisión
    cargarPuntosEmision(u.id);
  };

  const cargarPuntosEmision = async (empresaId) => {
    try {
      const res = await fetch(`${API}/api/empresa/${empresaId}/puntos-emision`, { headers });
      const data = await res.json();
      setPuntosEmision(data.puntosEmision || []);
    } catch (e) { setPuntosEmision([]); }
  };

  const agregarPuntoEmision = async () => {
    if (!nuevoPunto.establecimiento || !nuevoPunto.puntoEmision) {
      return show("Establecimiento y Punto de Emisión son requeridos", "error");
    }
    try {
      const res = await fetch(`${API}/api/empresa/${dialogEditar.empresa.id}/puntos-emision`, {
        method: "POST", headers, body: JSON.stringify(nuevoPunto)
      });
      const data = await res.json();
      if (data.success) {
        show("Punto de emisión agregado");
        cargarPuntosEmision(dialogEditar.empresa.id);
        setNuevoPunto({ establecimiento: "", puntoEmision: "", nombre: "", secuencialInicial: "1" });
      } else { show(data.error, "error"); }
    } catch (e) { show("Error de conexión", "error"); }
  };

  const editarSecuencialPunto = async (puntoId, nuevoSecuencial) => {
    try {
      const res = await fetch(`${API}/api/empresa/${dialogEditar.empresa.id}/puntos-emision/${puntoId}`, {
        method: "PUT", headers, body: JSON.stringify({ secuencialInicial: nuevoSecuencial })
      });
      const data = await res.json();
      if (data.success) {
        show("Secuencial actualizado");
        cargarPuntosEmision(dialogEditar.empresa.id);
      } else { show(data.error, "error"); }
    } catch (e) { show("Error", "error"); }
  };

  const eliminarPuntoEmision = async (puntoId) => {
    if (!window.confirm("¿Eliminar este punto de emisión?")) return;
    try {
      const res = await fetch(`${API}/api/empresa/${dialogEditar.empresa.id}/puntos-emision/${puntoId}`, {
        method: "DELETE", headers
      });
      const data = await res.json();
      if (data.success) {
        show("Punto eliminado");
        cargarPuntosEmision(dialogEditar.empresa.id);
      } else { show(data.error, "error"); }
    } catch (e) { show("Error", "error"); }
  };

  const guardarEdicion = async () => {
    setLoading(true);
    try {
      // 1. Actualizar credenciales si cambiaron
      if (editForm.nombre || editForm.email || editForm.newPassword) {
        const credPayload = {};
        if (editForm.nombre) credPayload.nombre = editForm.nombre;
        if (editForm.email) credPayload.email = editForm.email;
        if (editForm.newPassword) credPayload.password = editForm.newPassword;
        
        const credRes = await fetch(`${API}/api/usuarios/${dialogEditar.empresa.id}/credenciales`, {
          method: "PUT", headers, body: JSON.stringify(credPayload),
        });
        const credData = await credRes.json();
        if (!credRes.ok) {
          show(credData.error || "Error al actualizar credenciales", "error");
          setLoading(false);
          return;
        }
      }

      // 2. Actualizar datos de empresa
      const { nombre, email, newPassword, ...empresaData } = editForm;
      const res = await fetch(`${API}/api/empresa/${dialogEditar.empresa.id}`, {
        method: "PUT", headers, body: JSON.stringify({ nombre, ...empresaData }),
      });
      const data = await res.json();
      if (data.success) {
        // Subir logo si se seleccionó
        if (editLogoFile) {
          const logoData = new FormData();
          logoData.append("logo", editLogoFile);
          logoData.append("userId", dialogEditar.empresa.id);
          await fetch(`${API}/api/empresa/logo`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: logoData,
          });
        }
        show("Empresa actualizada correctamente");
        setDialogEditar({ open: false, empresa: null });
        cargarUsuarios();
      } else {
        show(data.error || "Error al actualizar", "error");
      }
    } catch (err) {
      show("Error de conexión", "error");
    } finally { setLoading(false); }
  };

  const backupEmpresa = async (empresaId, ruc) => {
    try {
      const res = await fetch(`${API}/api/empresa/${empresaId}/backup`, { headers });
      if (!res.ok) {
        const err = await res.json();
        return show(err.error || "Error al generar backup", "error");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_${ruc}_${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      show("Backup descargado correctamente");
    } catch (e) {
      show("Error de conexión al generar backup", "error");
    }
  };

  const eliminarEmpresa = async (empresaId, nombre) => {
    if (!window.confirm(`¿ELIMINAR la empresa "${nombre}" y todos sus datos?\n\nEsta acción NO se puede deshacer.\nSe recomienda hacer backup primero.`)) return;
    if (!window.confirm(`CONFIRMAR: ¿Está seguro de eliminar "${nombre}"?`)) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/empresa/${empresaId}`, {
        method: "DELETE", headers,
      });
      const data = await res.json();
      if (data.success) {
        show(data.message);
        cargarUsuarios();
      } else {
        show(data.error || "Error al eliminar", "error");
      }
    } catch (e) {
      show("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  const calcularDiasRestantes = (fechaCreacion, tipoPlan) => {
    if (!fechaCreacion) return null;
    const diasPlan = tipoPlan === "anual" ? 365 : 30;
    const fechaInicio = new Date(fechaCreacion);
    const fechaExpiracion = new Date(fechaInicio);
    fechaExpiracion.setDate(fechaExpiracion.getDate() + diasPlan);
    
    const hoy = new Date();
    const diferencia = Math.ceil((fechaExpiracion - hoy) / (1000 * 60 * 60 * 24));
    return { diasRestantes: diferencia, expirado: diferencia < 0 };
  };

  const empresas = usuarios.filter(u => u.role === "ADMINISTRADOR");
  const cajeros = usuarios.filter(u => u.role === "CAJERO");

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Business /> Gestión de Empresas y Usuarios
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Crear Empresa" icon={<Add />} iconPosition="start" />
        <Tab label="Crear Cajero" icon={<PersonAdd />} iconPosition="start" />
        <Tab label="Listado" />
      </Tabs>

      {/* TAB 0: Crear Empresa */}
      {tab === 0 && (
        <Paper sx={{ p: 3, maxWidth: 900 }}>
          <form onSubmit={crearEmpresa}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Datos del Administrador
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Nombre completo *" name="nombre"
                  value={form.nombre} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Email *" name="email" type="email"
                  value={form.email} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Contraseña *" name="password" type="password"
                  value={form.password} onChange={handleChange} required />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Datos de la Empresa
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Razón Social *" name="razonSocial"
                  value={form.razonSocial} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Nombre Comercial" name="nombreComercial"
                  value={form.nombreComercial} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="RUC (13 dígitos) *" name="ruc"
                  value={form.ruc} onChange={handleChange} required
                  inputProps={{ maxLength: 13 }} />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth size="small" label="Dirección Matriz" name="direccion"
                  value={form.direccion} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Sucursal (opcional)" name="sucursal"
                  value={form.sucursal} onChange={handleChange}
                  placeholder="Ej: Sucursal Norte" />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Establecimiento" name="establecimiento"
                  value={form.establecimiento} onChange={handleChange}
                  placeholder="001" inputProps={{ maxLength: 3 }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Punto de Emisión" name="ptoEmision"
                  value={form.ptoEmision} onChange={handleChange}
                  placeholder="001" inputProps={{ maxLength: 3 }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Obligado Contabilidad</InputLabel>
                  <Select name="obligadoContabilidad" value={form.obligadoContabilidad}
                    label="Obligado Contabilidad" onChange={handleChange}>
                    <MenuItem value="SI">SI</MenuItem>
                    <MenuItem value="NO">NO</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ambiente SRI</InputLabel>
                  <Select name="ambiente" value={form.ambiente}
                    label="Ambiente SRI" onChange={handleChange}>
                    <MenuItem value="1">Pruebas</MenuItem>
                    <MenuItem value="2">Producción</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Contribuyente RIMPE" name="contribuyenteRimpe"
                  value={form.contribuyenteRimpe} onChange={handleChange}
                  placeholder="CONTRIBUYENTE NEGOCIO POPULAR - RÉGIMEN RIMPE" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Contribuyente</InputLabel>
                  <Select name="tipoContribuyente" value={form.tipoContribuyente}
                    label="Tipo de Contribuyente" onChange={handleChange}>
                    <MenuItem value="CONTRIBUYENTE NEGOCIO POPULAR - RÉGIMEN RIMPE">RIMPE - Negocio Popular</MenuItem>
                    <MenuItem value="CONTRIBUYENTE RÉGIMEN RIMPE">RIMPE</MenuItem>
                    <MenuItem value="CONTRIBUYENTE RÉGIMEN GENERAL">Régimen General</MenuItem>
                    <MenuItem value="RISE">RISE</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Secuencial Inicial" name="secuencialInicial"
                  value={form.secuencialInicial} onChange={handleChange}
                  type="number" inputProps={{ min: 1 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Plan</InputLabel>
                  <Select name="tipoPlan" value={form.tipoPlan}
                    label="Tipo de Plan" onChange={handleChange}>
                    <MenuItem value="mensual">Mensual (30 días)</MenuItem>
                    <MenuItem value="anual">Anual (365 días)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Archivos
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Button variant="outlined" component="label" startIcon={<Upload />} fullWidth
                  sx={{ height: 56, justifyContent: "flex-start" }}>
                  {logoFile ? logoFile.name : "Subir Logo (PNG/JPG)"}
                  <input type="file" hidden accept=".png,.jpg,.jpeg"
                    onChange={(e) => setLogoFile(e.target.files[0])} />
                </Button>
                {logoFile && (
                  <Box sx={{ mt: 1, textAlign: "center" }}>
                    <img src={URL.createObjectURL(logoFile)} alt="Preview"
                      style={{ maxHeight: 60, maxWidth: "100%", borderRadius: 4 }} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button variant="outlined" component="label" startIcon={<Upload />} fullWidth
                  sx={{ height: 56, justifyContent: "flex-start" }}>
                  {firmaFile ? firmaFile.name : "Firma Electrónica (.p12)"}
                  <input type="file" hidden accept=".p12"
                    onChange={(e) => setFirmaFile(e.target.files[0])} />
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Contraseña de la firma"
                  type="password" value={firmaPassword}
                  onChange={(e) => setFirmaPassword(e.target.value)}
                  disabled={!firmaFile}
                  sx={{ mt: "4px" }} />
              </Grid>
            </Grid>

            <Button type="submit" variant="contained" size="large" disabled={loading}
              sx={{ minWidth: 200 }}>
              {loading ? "Creando..." : "Crear Empresa"}
            </Button>
          </form>
        </Paper>
      )}

      {/* TAB 1: Crear Cajero */}
      {tab === 1 && (
        <Paper sx={{ p: 3, maxWidth: 600 }}>
          <form onSubmit={crearCajero}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Nuevo Cajero
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Empresa</InputLabel>
                  <Select value={cajeroForm.empresaId} label="Empresa"
                    onChange={(e) => setCajeroForm({ ...cajeroForm, empresaId: e.target.value })}>
                    {empresas.map(emp => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.empresa?.nombreComercial || emp.nombre} — {emp.empresa?.ruc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Nombre *" value={cajeroForm.nombre}
                  onChange={(e) => setCajeroForm({ ...cajeroForm, nombre: e.target.value })} required />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Email *" type="email" value={cajeroForm.email}
                  onChange={(e) => setCajeroForm({ ...cajeroForm, email: e.target.value })} required />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Contraseña *" type="password" value={cajeroForm.password}
                  onChange={(e) => setCajeroForm({ ...cajeroForm, password: e.target.value })} required />
              </Grid>
            </Grid>
            <Button type="submit" variant="contained" sx={{ mt: 3 }} disabled={loading}>
              {loading ? "Creando..." : "Crear Cajero"}
            </Button>
          </form>
        </Paper>
      )}

      {/* TAB 2: Listado */}
      {tab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Empresas ({empresas.length})
          </Typography>
          <TableContainer sx={{ mb: 4, overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase" } }}>
                  <TableCell>Nombre Comercial</TableCell>
                  <TableCell>RUC</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Establecimiento</TableCell>
                  <TableCell>Secuencial</TableCell>
                  <TableCell>Ambiente</TableCell>
                  <TableCell>Tipo Plan</TableCell>
                  <TableCell>Días Restantes</TableCell>
                  <TableCell>Firma</TableCell>
                  <TableCell align="center">Activo</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {empresas.map(u => {
                  const diasInfo = calcularDiasRestantes(u.fechaCreacion, u.empresa?.tipoPlan || "mensual");
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{u.empresa?.nombreComercial || u.nombre}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.empresa?.razonSocial}</Typography>
                      </TableCell>
                      <TableCell>{u.empresa?.ruc}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.empresa?.establecimiento}-{u.empresa?.ptoEmision}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          {u.empresa?.secuencialInicial || "1"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={u.empresa?.ambiente === "2" ? "Producción" : "Pruebas"}
                          color={u.empresa?.ambiente === "2" ? "success" : "warning"} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" 
                          label={u.empresa?.tipoPlan === "anual" ? "Anual" : "Mensual"}
                          color={u.empresa?.tipoPlan === "anual" ? "primary" : "info"} 
                          variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {diasInfo ? (
                          <Chip size="small" 
                            label={diasInfo.expirado ? "Expirado" : `${diasInfo.diasRestantes} días`}
                            color={diasInfo.expirado ? "error" : diasInfo.diasRestantes < 7 ? "warning" : "success"}
                            variant={diasInfo.expirado ? "filled" : "outlined"} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={u.tieneFirma ? "Sí" : "No"}
                          color={u.tieneFirma ? "success" : "error"} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Switch checked={u.activo !== false} size="small"
                          onChange={() => toggleActivo(u.id, u.activo)} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar Empresa">
                          <IconButton size="small" color="default" onClick={() => abrirDialogEditar(u)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Subir/Actualizar Firma">
                          <IconButton size="small" color="primary" onClick={() => abrirDialogFirma(u.id)}>
                            <VpnKey fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modificar Secuencial">
                          <IconButton size="small" color="secondary" 
                            onClick={() => abrirDialogSecuencial(u.id, u.id, u.empresa?.secuencialInicial)}>
                            <Numbers fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Descargar Backup">
                          <IconButton size="small" color="info"
                            onClick={() => backupEmpresa(u.id, u.empresa?.ruc)}>
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar Empresa">
                          <IconButton size="small" color="error"
                            onClick={() => eliminarEmpresa(u.id, u.empresa?.nombreComercial || u.nombre)}>
                            <DeleteForever fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {empresas.length === 0 && (
                  <TableRow><TableCell colSpan={11} align="center" sx={{ py: 3, color: "text.secondary" }}>
                    No hay empresas registradas
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Cajeros ({cajeros.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase" } }}>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell align="center">Activo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cajeros.map(u => {
                  const emp = empresas.find(e => e.id === u.empresaId);
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>{u.nombre}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{emp?.empresa?.nombreComercial || "—"}</TableCell>
                      <TableCell align="center">
                        <Switch checked={u.activo !== false} size="small"
                          onChange={() => toggleActivo(u.id, u.activo)} />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {cajeros.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                    No hay cajeros registrados
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Dialog Editar Empresa */}
      <Dialog open={dialogEditar.open} onClose={() => setDialogEditar({ open: false, empresa: null })} maxWidth="md" fullWidth>
        <DialogTitle>Editar Empresa: {dialogEditar.empresa?.empresa?.nombreComercial || dialogEditar.empresa?.nombre}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Credenciales del usuario */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} color="primary">Credenciales del Usuario</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Nombre del Usuario" value={editForm.nombre || ""}
                onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Email" value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Nueva Contraseña" type="password" value={editForm.newPassword || ""}
                onChange={(e) => setEditForm({ ...editForm, newPassword: e.target.value })}
                placeholder="Dejar vacío para no cambiar" />
            </Grid>
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} color="primary">Datos de la Empresa</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Razón Social" value={editForm.razonSocial || ""}
                onChange={(e) => setEditForm({ ...editForm, razonSocial: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Nombre Comercial" value={editForm.nombreComercial || ""}
                onChange={(e) => setEditForm({ ...editForm, nombreComercial: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Dirección Matriz" value={editForm.direccion || ""}
                onChange={(e) => setEditForm({ ...editForm, direccion: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Sucursal" value={editForm.sucursal || ""}
                onChange={(e) => setEditForm({ ...editForm, sucursal: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Establecimiento" value={editForm.establecimiento || ""}
                onChange={(e) => setEditForm({ ...editForm, establecimiento: e.target.value })} inputProps={{ maxLength: 3 }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Punto Emisión" value={editForm.ptoEmision || ""}
                onChange={(e) => setEditForm({ ...editForm, ptoEmision: e.target.value })} inputProps={{ maxLength: 3 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Obligado Contabilidad</InputLabel>
                <Select value={editForm.obligadoContabilidad || "NO"} label="Obligado Contabilidad"
                  onChange={(e) => setEditForm({ ...editForm, obligadoContabilidad: e.target.value })}>
                  <MenuItem value="SI">SI</MenuItem>
                  <MenuItem value="NO">NO</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Ambiente SRI</InputLabel>
                <Select value={editForm.ambiente || "2"} label="Ambiente SRI"
                  onChange={(e) => setEditForm({ ...editForm, ambiente: e.target.value })}>
                  <MenuItem value="1">Pruebas</MenuItem>
                  <MenuItem value="2">Producción</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Plan</InputLabel>
                <Select value={editForm.tipoPlan || "mensual"} label="Tipo Plan"
                  onChange={(e) => setEditForm({ ...editForm, tipoPlan: e.target.value })}>
                  <MenuItem value="mensual">Mensual</MenuItem>
                  <MenuItem value="anual">Anual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Contribuyente RIMPE" value={editForm.contribuyenteRimpe || ""}
                onChange={(e) => setEditForm({ ...editForm, contribuyenteRimpe: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Contribuyente</InputLabel>
                <Select value={editForm.tipoContribuyente || ""} label="Tipo Contribuyente"
                  onChange={(e) => setEditForm({ ...editForm, tipoContribuyente: e.target.value })}>
                  <MenuItem value="CONTRIBUYENTE NEGOCIO POPULAR - RÉGIMEN RIMPE">RIMPE - Negocio Popular</MenuItem>
                  <MenuItem value="CONTRIBUYENTE RÉGIMEN RIMPE">RIMPE</MenuItem>
                  <MenuItem value="CONTRIBUYENTE RÉGIMEN GENERAL">Régimen General</MenuItem>
                  <MenuItem value="RISE">RISE</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                Puntos de Emisión
              </Typography>
              
              {/* Lista de puntos existentes */}
              {puntosEmision.length > 0 && (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Estab.</strong></TableCell>
                      <TableCell><strong>Pto. Emi.</strong></TableCell>
                      <TableCell><strong>Nombre</strong></TableCell>
                      <TableCell><strong>Secuencial</strong></TableCell>
                      <TableCell align="center"><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {puntosEmision.map(p => (
                      <TableRow key={p.id}>
                        <TableCell>{p.establecimiento}</TableCell>
                        <TableCell>{p.puntoEmision}</TableCell>
                        <TableCell>{p.nombre}</TableCell>
                        <TableCell>
                          <TextField size="small" type="number" defaultValue={p.secuencialInicial || 1}
                            sx={{ width: 100 }}
                            onBlur={(e) => {
                              const val = parseInt(e.target.value);
                              if (val && val !== p.secuencialInicial) editarSecuencialPunto(p.id, val);
                            }}
                            inputProps={{ min: 1 }} />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="error" onClick={() => eliminarPuntoEmision(p.id)}>
                            <DeleteForever fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Agregar nuevo punto */}
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={2}>
                  <TextField size="small" label="Estab." value={nuevoPunto.establecimiento}
                    onChange={(e) => setNuevoPunto({ ...nuevoPunto, establecimiento: e.target.value })}
                    inputProps={{ maxLength: 3 }} placeholder="001" />
                </Grid>
                <Grid item xs={2}>
                  <TextField size="small" label="Pto. Emi." value={nuevoPunto.puntoEmision}
                    onChange={(e) => setNuevoPunto({ ...nuevoPunto, puntoEmision: e.target.value })}
                    inputProps={{ maxLength: 3 }} placeholder="001" />
                </Grid>
                <Grid item xs={3}>
                  <TextField size="small" label="Nombre" value={nuevoPunto.nombre}
                    onChange={(e) => setNuevoPunto({ ...nuevoPunto, nombre: e.target.value })}
                    placeholder="Local Centro" fullWidth />
                </Grid>
                <Grid item xs={2}>
                  <TextField size="small" label="Sec. Inicial" type="number" value={nuevoPunto.secuencialInicial}
                    onChange={(e) => setNuevoPunto({ ...nuevoPunto, secuencialInicial: e.target.value })}
                    inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={3}>
                  <Button variant="contained" size="small" onClick={agregarPuntoEmision} startIcon={<Add />}>
                    Agregar
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Button variant="outlined" component="label" startIcon={<Upload />} fullWidth>
                {editLogoFile ? editLogoFile.name : "Cambiar Logo (PNG/JPG)"}
                <input type="file" hidden accept=".png,.jpg,.jpeg"
                  onChange={(e) => setEditLogoFile(e.target.files[0])} />
              </Button>
              {editLogoFile && (
                <Box sx={{ mt: 1, textAlign: "center" }}>
                  <img src={URL.createObjectURL(editLogoFile)} alt="Preview"
                    style={{ maxHeight: 60, maxWidth: "100%", borderRadius: 4 }} />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogEditar({ open: false, empresa: null })}>Cancelar</Button>
          <Button onClick={guardarEdicion} variant="contained" disabled={loading}>
            {loading ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Subir Firma */}
      <Dialog open={dialogFirma.open} onClose={() => setDialogFirma({ open: false, userId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Subir/Actualizar Firma Electrónica</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Button variant="outlined" component="label" startIcon={<Upload />} fullWidth sx={{ mb: 2 }}>
              {editFirmaFile ? editFirmaFile.name : "Seleccionar archivo .p12"}
              <input type="file" hidden accept=".p12" onChange={(e) => setEditFirmaFile(e.target.files[0])} />
            </Button>
            <TextField fullWidth label="Contraseña de la firma" type="password"
              value={editFirmaPassword} onChange={(e) => setEditFirmaPassword(e.target.value)}
              disabled={!editFirmaFile} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogFirma({ open: false, userId: null })}>Cancelar</Button>
          <Button onClick={subirFirma} variant="contained" disabled={loading || !editFirmaFile || !editFirmaPassword}>
            {loading ? "Subiendo..." : "Subir Firma"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Modificar Secuenciales */}
      <Dialog open={dialogSecuencial.open} onClose={() => setDialogSecuencial({ open: false, userId: null, empresaId: null, secuencial: "" })} maxWidth="sm" fullWidth>
        <DialogTitle>Modificar Secuenciales</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField fullWidth label="Secuencial Facturas" type="number"
              value={dialogSecuencial.secuencial}
              onChange={(e) => setDialogSecuencial({ ...dialogSecuencial, secuencial: e.target.value })}
              inputProps={{ min: 1 }}
              helperText="Próximo número de factura" />
            <TextField fullWidth label="Secuencial Guías de Remisión" type="number"
              value={dialogSecuencial.secuencialGuia || ""}
              onChange={(e) => setDialogSecuencial({ ...dialogSecuencial, secuencialGuia: e.target.value })}
              inputProps={{ min: 1 }}
              helperText="Próximo número de guía de remisión" />
            <TextField fullWidth label="Secuencial Liquidaciones de Compra" type="number"
              value={dialogSecuencial.secuencialLiquidacion || ""}
              onChange={(e) => setDialogSecuencial({ ...dialogSecuencial, secuencialLiquidacion: e.target.value })}
              inputProps={{ min: 1 }}
              helperText="Próximo número de liquidación de compra" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogSecuencial({ open: false, userId: null, empresaId: null, secuencial: "" })}>
            Cancelar
          </Button>
          <Button onClick={actualizarSecuencial} variant="contained" disabled={loading}>
            {loading ? "Actualizando..." : "Actualizar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={snackbar.sev} variant="filled">{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
